/**
 * Permissions Admin API client.
 * Calls /api/admin/permissions/* — admin-only endpoints for granting/revoking
 * dashboard access on Culture Genesis users.
 */

import { apiRequest } from './api';
import type { PermissionKey } from '../hooks/usePermissions';

const BASE = '/api/admin/permissions';

export interface PermissionDescriptor {
  key: PermissionKey;
  label: string;
  description: string;
  group: 'dashboard' | 'integration';
}

export interface PermissionUserRow {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  organization: string | null;
  /** ISO-8601 of the last sign-in the backend recorded, or null. */
  lastSignIn: string | null;
  permissions: Partial<Record<PermissionKey, boolean>>;
  /**
   * Other `users` docs carrying the same email (legacy uids from before the
   * deterministic-uid sign-in). Informational — grants always target `uid`.
   */
  duplicateUids?: string[];
}

export interface PendingPermissionRow {
  email: string;
  permissions: Partial<Record<PermissionKey, boolean>>;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
}

export async function getPermissionRegistry(): Promise<PermissionDescriptor[]> {
  const body = await apiRequest(`${BASE}/registry`);
  return body.registry as PermissionDescriptor[];
}

export async function listPermissionAssignments(): Promise<{
  users: PermissionUserRow[];
  pending: PendingPermissionRow[];
}> {
  return apiRequest(`${BASE}/users`);
}

export async function grantPermission(
  email: string,
  key: PermissionKey,
): Promise<{ status: 'granted' | 'pending'; uid?: string; duplicateUids?: string[] }> {
  return apiRequest(`${BASE}/grant`, {
    method: 'POST',
    body: JSON.stringify({ email, key }),
  });
}

export async function revokePermission(
  email: string,
  key: PermissionKey,
): Promise<{ status: 'revoked' }> {
  return apiRequest(`${BASE}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ email, key }),
  });
}

/**
 * Fully offboard a user: disables their Firebase account, revokes sessions, and
 * clears stored Google/Slack tokens plus all dashboard permissions. Irreversible
 * from the UI (re-enable from the Firebase console). Admins cannot offboard themselves.
 */
export async function offboardUser(
  email: string,
): Promise<{ status: 'offboarded'; uid: string; email: string | null }> {
  return apiRequest(`${BASE}/offboard`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}
